import React from 'react';
import { Alert, Box, Grid, LinearProgress, Stack, Typography } from '@mui/material';
import { useEnergy } from '../context/EnergyContext';
import { formatValue, signedPowerLabel } from '../utils/energyViewModel';
import {
  EnergyLineChart,
  EnergyPageHeader,
  MetricTile,
  ProvenanceNote,
  SectionCard,
} from '../components/energy/EnergyUi';

const BatteryCabinet = ({ bess }) => {
  const soc = Number.isFinite(Number(bess.socPct)) ? Math.max(0, Math.min(100, Number(bess.socPct))) : 0;
  const available = bess.socPct !== null;
  return (
    <SectionCard title="Battery cabinet" subtitle="SOC fill is driven only by an observed value" sx={{ height: '100%' }}>
      <Stack alignItems="center" spacing={1.5}>
        <Box sx={{ width: 180, height: 330, border: '4px solid', borderColor: available ? 'success.dark' : 'grey.500', borderRadius: 1, p: 1, display: 'flex', flexDirection: 'column-reverse', position: 'relative', bgcolor: 'grey.100' }}>
          <Box sx={{ height: available ? `${soc}%` : 0, bgcolor: 'success.main', transition: 'height .5s', borderRadius: 0.5 }} />
          {bess.maxSocPct !== null && <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: `${bess.maxSocPct}%`, borderTop: '2px dashed #1976d2' }} />}
          {bess.minSocPct !== null && <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: `${bess.minSocPct}%`, borderTop: '2px dashed #d32f2f' }} />}
        </Box>
        <Typography variant="h4" fontWeight={700}>{formatValue(bess.socPct, '%')}</Typography>
        <Typography variant="body2">{bess.pcsKw === 0 ? 'Standby' : signedPowerLabel(bess.pcsKw, 'Discharging', 'Charging')} at {formatValue(bess.pcsKw, 'kW')}</Typography>
        <Typography variant="caption" color="text.secondary">Reserve {formatValue(bess.minSocPct, '%')} · Maximum {formatValue(bess.maxSocPct, '%')}</Typography>
      </Stack>
    </SectionCard>
  );
};

export default function BessView() {
  const { model } = useEnergy();
  const { bess } = model;
  const dispatchDelta = bess.pcsKw !== null && bess.constrainedKw !== null ? bess.pcsKw - bess.constrainedKw : null;
  const visibleEquipment = (bess.equipment || []).filter((item) => {
    const manufacturer = String(item.manufacturer || '').trim().toLowerCase();
    const itemModel = String(item.model || '').trim().toLowerCase();
    return (
      manufacturer !== 'sabal simulator'
      && itemModel !== 'external-bidirectional-pcs'
      && itemModel !== 'two-rack-external-pcs'
    );
  });
  return (
    <Box>
      <EnergyPageHeader title="BESS Inspection" subtitle="Battery, BMS, PCS, dispatch response, limits, and equipment lineage" back />
      <Grid container spacing={2}>
        <Grid item xs={12} lg={4}>
          <Stack spacing={2}>
            <SectionCard title="Operational mode" subtitle={bess.pcsKw === 0 ? 'Standby' : (bess.state || '-')}>
              <Grid container spacing={1}>
                <Grid item xs={6}><MetricTile label="Requested" value={bess.requestedKw} unit="kW" accent="#00897b" /></Grid>
                <Grid item xs={6}><MetricTile label="Constrained" value={bess.constrainedKw} unit="kW" accent="#ed6c02" /></Grid>
                <Grid item xs={6}><MetricTile label="Actual PCS" value={bess.pcsKw} unit="kW" accent="#388e3c" state={bess.pcsKw === 0 ? 'Standby' : signedPowerLabel(bess.pcsKw, 'Discharging', 'Charging')} /></Grid>
                <Grid item xs={6}><MetricTile label="Response delta" value={dispatchDelta} unit="kW" accent="#1976d2" /></Grid>
              </Grid>
              <Alert severity={bess.limitingConstraint ? 'warning' : 'info'} sx={{ mt: 1.5 }}>
                {bess.limitingConstraint || 'No limiting-constraint result is available.'}
              </Alert>
            </SectionCard>
            <SectionCard title="State of charge">
              <Typography variant="h4" color="primary.main" fontWeight={700}>{formatValue(bess.socPct, '%')}</Typography>
              <LinearProgress variant="determinate" value={bess.socPct || 0} sx={{ height: 10, borderRadius: 2, my: 1 }} />
              <Typography variant="body2">Stored energy: {formatValue(bess.storedKwh, 'kWh')}</Typography>
              <Typography variant="body2">Configured operating energy window: {formatValue(bess.usableCapacityKwh, 'kWh')}</Typography>
              <Typography variant="body2">Usable above reserve: {formatValue(bess.usableKwh, 'kWh')}</Typography>
              <Typography variant="body2">Available charge: {formatValue(bess.availableChargeKw, 'kW')}</Typography>
              <Typography variant="body2">Available discharge: {formatValue(bess.availableDischargeKw, 'kW')}</Typography>
            </SectionCard>
            <SectionCard title="Electrical and thermal telemetry">
              <Grid container spacing={1}>
                <Grid item xs={6}><MetricTile label="DC voltage" value={bess.dcVoltageV} unit="V" /></Grid>
                <Grid item xs={6}><MetricTile label="DC current" value={bess.dcCurrentA} unit="A" /></Grid>
                <Grid item xs={6}><MetricTile label="AC voltage" value={bess.acVoltageV} unit="V" /></Grid>
                <Grid item xs={6}><MetricTile label="AC current" value={bess.acCurrentA} unit="A" /></Grid>
                <Grid item xs={6}><MetricTile label="Frequency" value={bess.frequencyHz} unit="Hz" /></Grid>
                <Grid item xs={6}><MetricTile label="State of health" value={bess.sohPct} unit="%" /></Grid>
                <Grid item xs={4}><MetricTile label="Cell" value={bess.cellTempC} unit="°C" accent="#ed6c02" /></Grid>
                <Grid item xs={4}><MetricTile label="Ambient" value={bess.ambientTempC} unit="°C" accent="#0288d1" /></Grid>
                <Grid item xs={4}><MetricTile label="PCS" value={bess.pcsTempC} unit="°C" accent="#d32f2f" /></Grid>
              </Grid>
            </SectionCard>
          </Stack>
        </Grid>
        <Grid item xs={12} lg={4}><BatteryCabinet bess={bess} /></Grid>
        <Grid item xs={12} lg={4}>
          <Stack spacing={2}>
            <SectionCard title="SOC and site context" subtitle="Selected range; gaps remain gaps">
              <EnergyLineChart series={model.series} fields={[{ key: 'socPct', label: 'SOC %' }, { key: 'pcsKw', label: 'PCS kW' }, { key: 'requestedKw', label: 'Requested kW', color: '#1565c0' }, { key: 'constrainedKw', label: 'Constrained kW', color: '#ef6c00' }]} />
            </SectionCard>
            <SectionCard title="Solar, load, grid, and battery">
              <EnergyLineChart series={model.series} fields={[{ key: 'solarKw', label: 'Solar' }, { key: 'loadKw', label: 'Load' }, { key: 'bessKw', label: 'Battery' }, { key: 'gridKw', label: 'Grid' }]} />
            </SectionCard>
            <SectionCard title="Period battery performance">
              <Grid container spacing={1}>
                <Grid item xs={6}><MetricTile label="AC energy throughput" value={bess.efficiency?.acThroughputKwh} measure={model.measures?.bess?.efficiency?.acThroughputKwh} unit="kWh" state={bess.efficiency?.status} /></Grid>
                <Grid item xs={6}><MetricTile label="Discharge equivalent cycles" value={bess.efficiency?.acDischargeEquivalentCycles} measure={model.measures?.bess?.efficiency?.acDischargeEquivalentCycles} /></Grid>
                <Grid item xs={6}><MetricTile label="Configured round-trip efficiency" value={bess.efficiency?.configuredRoundTripEfficiencyPct} measure={model.measures?.bess?.efficiency?.configuredRoundTripEfficiencyPct} unit="%" provenance="Configured" /></Grid>
                <Grid item xs={6}><MetricTile label="Observed round-trip efficiency" value={bess.efficiency?.observedAcRoundTripEfficiencyPct} measure={model.measures?.bess?.efficiency?.observedAcRoundTripEfficiencyPct} unit="%" state={bess.efficiency?.observedAcRoundTripEfficiencyStatus} /></Grid>
              </Grid>
              <ProvenanceNote>Observed efficiency requires compatible stored-energy endpoints and unchanged equipment and configuration.</ProvenanceNote>
            </SectionCard>
            <SectionCard title="Equipment inventory">
              {visibleEquipment.length ? visibleEquipment.map((item, index) => (
                <Box key={item.equipmentId || item.id || index} sx={{ py: 1, borderBottom: index < visibleEquipment.length - 1 ? '1px solid' : 0, borderColor: 'divider' }}>
                  <Typography variant="body2" fontWeight={600}>{item.manufacturer || '-'} {item.model || ''}</Typography>
                  <Typography variant="caption" color="text.secondary">ID {item.equipmentId || item.id || '-'} · Serial {item.serialNumber || '-'} · Firmware {item.firmwareVersion || '-'}</Typography>
                </Box>
              )) : <Typography variant="body2" color="text.secondary">-</Typography>}
              <ProvenanceNote>Equipment identity remains visible when telemetry is missing. Missing values display as -.</ProvenanceNote>
            </SectionCard>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
